/**
 * React Query Pagination Hook
 * Integrates pagination with React Query for data fetching
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { usePagination } from './use-pagination';
import {
  UsePaginatedQueryConfig,
  PaginatedResponse,
  PaginationParams,
} from '@/lib/pagination/types';

export function usePaginatedQuery<T>(config: UsePaginatedQueryConfig<T>) {
  const {
    queryKey,
    queryFn,
    filters = {},
    enabled = true,
    prefetchNext = true,
    prefetchPrevious = false,
    ...paginationConfig
  } = config;

  const queryClient = useQueryClient();
  const pagination = usePagination(paginationConfig);
  const { currentPage, pageSize, updatePaginationMeta } = pagination;

  // Build complete query key with pagination and filters
  const fullQueryKey = useMemo(
    () => [...queryKey, { page: currentPage, limit: pageSize, ...filters }],
    [queryKey, currentPage, pageSize, filters]
  );

  // Build query parameters
  const queryParams: PaginationParams & Record<string, any> = useMemo(
    () => ({
      page: currentPage,
      limit: pageSize,
      ...filters,
    }),
    [currentPage, pageSize, filters]
  );

  // Main query
  const query = useQuery<PaginatedResponse<T>>({
    queryKey: fullQueryKey,
    queryFn: () => queryFn(queryParams),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
  });

  // Update pagination meta when data changes
  useEffect(() => {
    if (query.data?.pagination) {
      updatePaginationMeta({
        totalPages: query.data.pagination.totalPages,
        totalCount: query.data.pagination.totalCount,
      });
    }
  }, [query.data, updatePaginationMeta]);

  // Prefetch next page
  useEffect(() => {
    if (prefetchNext && query.data && pagination.hasNext) {
      const nextQueryKey = [...queryKey, { page: currentPage + 1, limit: pageSize, ...filters }];

      queryClient.prefetchQuery({
        queryKey: nextQueryKey,
        queryFn: () =>
          queryFn({
            page: currentPage + 1,
            limit: pageSize,
            ...filters,
          }),
        staleTime: 1000 * 60 * 5, // 5 minutes
      });
    }
  }, [
    queryClient,
    queryKey,
    queryFn,
    currentPage,
    pageSize,
    filters,
    prefetchNext,
    query.data,
    pagination.hasNext,
  ]);

  // Prefetch previous page
  useEffect(() => {
    if (prefetchPrevious && query.data && pagination.hasPrevious) {
      const prevQueryKey = [...queryKey, { page: currentPage - 1, limit: pageSize, ...filters }];

      queryClient.prefetchQuery({
        queryKey: prevQueryKey,
        queryFn: () =>
          queryFn({
            page: currentPage - 1,
            limit: pageSize,
            ...filters,
          }),
        staleTime: 1000 * 60 * 5, // 5 minutes
      });
    }
  }, [
    queryClient,
    queryKey,
    queryFn,
    currentPage,
    pageSize,
    filters,
    prefetchPrevious,
    query.data,
    pagination.hasPrevious,
  ]);

  // Invalidate queries when filters change
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  return {
    // Query state
    data: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    isSuccess: query.isSuccess,

    // Pagination state and actions
    pagination: {
      ...pagination,
      meta: query.data?.pagination,
    },

    // Utility methods
    invalidate,
    refetch: query.refetch,
  };
}
